import { fetchData } from "../api";

type DataResponse = Spintr.IMarketplaceWidgetDataResponse;

interface ICompletedFetch {
    expires: Date;
    response: DataResponse;
}

interface IFetchHistory {
    [id: string]: ICompletedFetch;
}

interface IPendingFetches {
    [id: string]: Promise<DataResponse>;
}

interface IQueuedFetch {
    dataId: string;
    token: string | null;
    url: string | null;
    promise: Promise<DataResponse> | null;
    rejector: null | ((reason: any) => void);
    resolver: null | ((value: DataResponse) => void);
}

class MarketplaceDataLoader {
    public static remove(dataId: string): void {
        if (!MarketplaceDataLoader.history[dataId]) {
            return;
        }

        delete MarketplaceDataLoader.history[dataId];
    }

    public static async load(
        dataId: string,
        url: string | null = null,
        token: string | null = null
    ): Promise<DataResponse> {
        const now = new Date();

        if (MarketplaceDataLoader.history[dataId]) {
            const stored = MarketplaceDataLoader.history[dataId];
            
            if (stored.expires < now) {
                // Invalidate store if data has expired
                delete MarketplaceDataLoader.history[dataId];
            } else {
                // Return stored response
                return stored.response;
            }
        }

        const pendingPromise = MarketplaceDataLoader.pending[dataId];
        if (typeof pendingPromise !== "undefined") {
            // If we have an active fetch, return it when it's done
            return await pendingPromise;
        }

        if (MarketplaceDataLoader.queue.some(q => q.dataId === dataId)) {
            const item = MarketplaceDataLoader.queue.find(q => q.dataId === dataId);

            if (item !== undefined && item.promise !== null) {
                return await item.promise;
            }
        }

        const pendingItems = Object
            .keys(MarketplaceDataLoader.pending)
            .filter((key) => isNaN(+key));

        if (pendingItems.length >= MarketplaceDataLoader.maxConcurrentFetches) {
            // Add to queue if we're exceeding fetch limit
            return await MarketplaceDataLoader.queueFetch(dataId, url, token);
        }

        return await MarketplaceDataLoader.fetch(dataId, url, token);
    }

    private static readonly defaultTtl = 5 * 60 * 1000;
    private static readonly maxConcurrentFetches = 4;

    private static readonly history: IFetchHistory = {};
    private static readonly pending: IPendingFetches = {};
    private static readonly queue: IQueuedFetch[] = [];

    private static async fetch(dataId: string, url: string | null = null, token: string | null = null): Promise<DataResponse> {
        const promise = fetchData(dataId, url, token);
        MarketplaceDataLoader.pending[dataId] = promise;

        const response = await promise;
        delete MarketplaceDataLoader.pending[dataId];

        const now = new Date();
        MarketplaceDataLoader.history[dataId] = {
            expires: new Date(now.getTime() + MarketplaceDataLoader.defaultTtl),
            response,
        };

        MarketplaceDataLoader.unqueueNextFetch();

        return response;
    }

    private static unqueueNextFetch(): void {
        // TODO: Check if we have too many pending? 
        // Also, add to pending
        if (MarketplaceDataLoader.queue.length === 0) {
            return;            
        }

        const next = MarketplaceDataLoader.queue.pop();
        if (!next) {
            return;
        }

        if (next.promise === null || next.resolver === null || next.rejector === null) {
            MarketplaceDataLoader.queue.unshift(next);

            if (MarketplaceDataLoader.queue.length === 0) {
                setTimeout(() => {
                    MarketplaceDataLoader.unqueueNextFetch();
                }, 1000);
                return;
            }

            MarketplaceDataLoader.unqueueNextFetch();
            return;
        }
        
        fetchData(next.dataId, next.url, next.token)
            .then(next.resolver, next.rejector)
            .finally(() => {
                MarketplaceDataLoader.unqueueNextFetch();
            });
    }

    private static queueFetch(dataId: string, url: string | null = null, token: string | null = null) {
        const queuedFetch: IQueuedFetch = {
            dataId,
            token,
            url,
            promise: null,
            rejector: null,
            resolver: null,
        };

        queuedFetch.promise = new Promise<DataResponse>((resolve, reject) => {
            queuedFetch.resolver = resolve;
            queuedFetch.rejector = reject;
        });

        MarketplaceDataLoader.queue.unshift(queuedFetch);

        return queuedFetch.promise;
    }

    private constructor() { /* Unused, just to prevent instantiation */ }
}

export default MarketplaceDataLoader;
